import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import type { CatalogRecord } from '@okmaps/shared';
import { useAppDispatch, useAppSelector } from '../../app/hooks.js';
import { selectActiveLayerNames } from '../../store/selectors/index.js';
import { metadataOpened } from '../../store/slices/uiSlice.js';
import { addLayerToMap } from '../../store/thunks/addLayerToMap.js';
import { layerRemoved } from '../../store/slices/layersSlice.js';

interface Props {
  readonly record: CatalogRecord;
}

/**
 * One dataset.
 *
 * A record that cannot be drawn is shown and labeled rather than hidden. The
 * catalog and the capabilities documents are different sets - 188 records
 * against 363 drawable layers - and ten catalog entries have no drawable layer.
 * Hiding them would leave a user searching for something the upstream catalog
 * says exists.
 */
export default function RecordRow({ record }: Props) {
  const dispatch = useAppDispatch();
  const activeNames = useAppSelector(selectActiveLayerNames);
  const onMap = record.layerName !== undefined && activeNames.includes(record.layerName);

  const toggle = () => {
    if (!record.layerName) return;
    if (onMap) dispatch(layerRemoved(record.layerName));
    else void dispatch(addLayerToMap({ layerName: record.layerName, zoomTo: true }));
  };

  return (
    <TreeItem
      itemId={record.id}
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.25, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: record.renderable ? 'text.primary' : 'text.secondary',
            }}
            title={record.title}
          >
            {record.title}
          </Typography>

          {!record.renderable && (
            <Tooltip title="This catalog entry has no map service published for it">
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                metadata only
              </Typography>
            </Tooltip>
          )}

          <Tooltip title="Dataset details">
            <IconButton
              size="small"
              aria-label={`Details for ${record.title}`}
              onClick={(e) => {
                e.stopPropagation();
                dispatch(metadataOpened(record.id));
              }}
            >
              <InfoOutlined sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>

          {record.renderable && (
            <>
              <Tooltip title="Download">
                <IconButton
                  size="small"
                  aria-label={`Download ${record.title}`}
                  component="a"
                  href={`/api/download?typeName=${encodeURIComponent(record.layerName ?? '')}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <DownloadOutlined sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>

              <Tooltip title={onMap ? 'Remove from map' : 'Add to map'}>
                <IconButton
                  size="small"
                  color={onMap ? 'primary' : 'default'}
                  aria-label={`${onMap ? 'Remove' : 'Add'} ${record.title} ${onMap ? 'from' : 'to'} the map`}
                  aria-pressed={onMap}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle();
                  }}
                >
                  {onMap ? <CheckIcon sx={{ fontSize: 16 }} /> : <AddIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      }
    />
  );
}
